import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = window.__WR_API_BASE__ ? `${window.__WR_API_BASE__}/api` : '/api';

/**
 * useSessionSharing — Handles live session sharing (create, update, end).
 * Extracted from App.js to reduce monolith.
 */
const useSessionSharing = ({
  notes, patientAge, patientGender, patientStatus,
  imagingOrders, specialties, result,
}) => {
  const [shareCode, setShareCode] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const startShareSession = useCallback(async (durationHours = 72, dateRangeDays = 1) => {
    setIsSharing(true);
    try {
      const response = await axios.post(`${API}/session/share`, {
        notes, patient_info: { age: patientAge, gender: patientGender, status: patientStatus },
        imaging_orders: imagingOrders, specialties, result,
        duration_hours: durationHours,
        date_range_days: dateRangeDays
      }, { withCredentials: true });
      setShareCode(response.data.share_code);
      setShowShareModal(true);
      const durationLabel = response.data.duration_label || `${durationHours} hours`;
      toast.success(`Share session created! Valid for ${durationLabel}`);
    } catch (error) {
      toast.error('Failed to create share session');
    } finally {
      setIsSharing(false);
    }
  }, [notes, patientAge, patientGender, patientStatus, imagingOrders, specialties, result]);

  const updateShareSession = useCallback(async (immediateResult = null) => {
    if (!shareCode) return;
    try {
      await axios.put(`${API}/session/share/${shareCode}`, {
        notes, patient_info: { age: patientAge, gender: patientGender, status: patientStatus },
        imaging_orders: imagingOrders, specialties, result: immediateResult || result
      });
    } catch (error) {
      console.error('Failed to update share session:', error);
    }
  }, [shareCode, notes, patientAge, patientGender, patientStatus, imagingOrders, specialties, result]);

  const endShareSession = useCallback(async () => {
    if (!shareCode) return;
    try {
      await axios.delete(`${API}/session/share/${shareCode}`);
      setShareCode(null);
      setShowShareModal(false);
      toast.success('Share session ended');
    } catch (error) {
      console.error('Failed to end share session:', error);
    }
  }, [shareCode]);

  // Debounced update on data changes
  useEffect(() => {
    if (shareCode) {
      const debounce = setTimeout(() => updateShareSession(), 2000);
      return () => clearTimeout(debounce);
    }
  }, [shareCode, notes, patientAge, patientGender, patientStatus, imagingOrders, specialties, result]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    shareCode, setShareCode,
    isSharing,
    showShareModal, setShowShareModal,
    startShareSession, updateShareSession, endShareSession,
  };
};

export default useSessionSharing;
